import React, { useState } from 'react';
import { X, Shield, User, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
}

export const SignInModal: React.FC<SignInModalProps> = ({
  isOpen,
  onClose,
  title = 'Sign in to continue',
  message = 'Sign in to access your prayer history, track progress, and sync across devices.'
}) => {
  const { signIn, isLoading, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  if (!isOpen) return null;


  const handleManualSignIn = async () => {
    try {
      setIsSigningIn(true);
      await signIn();
      onClose();
    } catch (error) {
      setIsSigningIn(false);
      console.error('Manual sign-in error:', error);
    }
  };

  const benefits = [
    {
      icon: <User className="w-5 h-5" />,
      title: "Account Security",
      description: "Secure access to your subscription and preferences"
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      title: "Subscription Management",
      description: "Manage your premium subscription across devices"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Future Features",
      description: "Be ready for upcoming cloud sync and cross-device features"
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors duration-300">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center">
            <div className="bg-white bg-opacity-20 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{title}</h2>
            <p className="text-blue-100">{message}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Benefits */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Why sign in?
            </h3>
            <div className="space-y-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5">
                    {benefit.icon}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {benefit.title}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300 text-xs">
                      {benefit.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sign-in Options */}
          <div className="space-y-4">
            {/* Use AuthContext signIn method for consistent state management */}
            <div className="text-center">
              <button
                onClick={handleManualSignIn}
                disabled={isLoading || isSigningIn}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-3"
              >
                {isSigningIn ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="text-center">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}

            {/* Loading State */}
            {(isLoading || isSigningIn) && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Signing you in...
                </p>
              </div>
            )}
          </div>

          {/* Continue without account option */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Progress is saved locally on this device
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 py-2 text-sm transition-colors duration-200"
            >
              Continue without signing in
            </button>
          </div>

          {/* Privacy Notice */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
              <Shield className="w-3 h-3" />
              Your data is secure and private
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};